// Sherpa Publishing - Music Rights Registration App
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { RegistrationProvider, useRegistration } from './context/RegistrationContext';
import { HybridRegistrationProvider } from './context/HybridRegistrationContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AppLayout from './components/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';

// Minimal loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-spotify-green border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Lazy-loaded pages — split into separate chunks
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SongInputPage = lazy(() => import('./pages/SongInputPage'));
const WriterConfirmationPage = lazy(() => import('./pages/WriterConfirmationPage'));
const SplitsPage = lazy(() => import('./pages/SplitsPage'));
const SummaryPage = lazy(() => import('./pages/SummaryPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const HybridChatPage = lazy(() => import('./pages/HybridChatPage'));
const WritersPage = lazy(() => import('./pages/WritersPage'));

// Admin pages
const AdminWritersPage = lazy(() => import('./pages/admin/AdminWritersPage'));
const AdminWriterDetailPage = lazy(() => import('./pages/admin/AdminWriterDetailPage'));
const AdminUploadPage = lazy(() => import('./pages/admin/AdminUploadPage'));
const AdminMergesPage = lazy(() => import('./pages/admin/AdminMergesPage'));
const AdminMembersPage = lazy(() => import('./pages/admin/AdminMembersPage'));
const AdminClustersPage = lazy(() => import('./pages/admin/AdminClustersPage'));
const AdminScrapePage = lazy(() => import('./pages/admin/AdminScrapePage'));

const AdminWriterProfilePage = lazy(() => import('./pages/admin/AdminWriterProfilePage'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminRegistrationsPage = lazy(() => import('./pages/admin/AdminRegistrationsPage'));
const AdminRosterPage = lazy(() => import('./pages/admin/AdminRosterPage'));
const AdminExportsPage = lazy(() => import('./pages/admin/AdminExportsPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminAllOrgsPage = lazy(() => import('./pages/admin/AdminAllOrgsPage'));
const AdminAuditLogPage = lazy(() => import('./pages/admin/AdminAuditLogPage'));

// Page Router Component for Registration Flow
function RegistrationRouter() {
  const { state } = useRegistration();
  const { currentStep, registrationComplete } = state;

  // If registration is complete, show success (handled in SummaryPage)
  if (registrationComplete) {
    return <SummaryPage />;
  }

  // Route based on current step
  switch (currentStep) {
    case 1:
      return <SongInputPage />;
    case 2:
      return <WriterConfirmationPage />;
    case 3:
      return <SplitsPage />;
    case 4:
      return <SummaryPage />;
    default:
      return <SongInputPage />;
  }
}

// Registration Flow wrapper with its own provider
function RegistrationFlow() {
  return (
    <RegistrationProvider>
      <RegistrationRouter />
    </RegistrationProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <DashboardPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/writers"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <WritersPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-song"
                element={
                  <ProtectedRoute>
                    <RegistrationFlow />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <AppLayout fullHeight>
                      <ChatPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hybrid"
                element={
                  <ProtectedRoute>
                    <HybridRegistrationProvider>
                      <AppLayout fullHeight>
                        <HybridChatPage />
                      </AppLayout>
                    </HybridRegistrationProvider>
                  </ProtectedRoute>
                }
              />

              {/* Admin routes — nested under AdminLayout with sidebar */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminOverviewPage />} />
                <Route path="registrations" element={<AdminRegistrationsPage />} />
                <Route path="roster" element={<AdminRosterPage />} />
                <Route path="roster/:identityId" element={<AdminWriterProfilePage />} />
                <Route path="exports" element={<AdminExportsPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="orgs" element={<AdminAllOrgsPage />} />
                <Route path="audit" element={<AdminAuditLogPage />} />
                {/* Legacy admin pages */}
                <Route path="writers" element={<AdminWritersPage />} />
                <Route path="writers/upload" element={<AdminUploadPage />} />
                <Route path="writers/:id" element={<AdminWriterDetailPage />} />
                <Route path="merges" element={<AdminMergesPage />} />
                <Route path="org/members" element={<AdminMembersPage />} />
                <Route path="clusters" element={<AdminClustersPage />} />
                <Route path="scrape" element={<AdminScrapePage />} />
              </Route>

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
